import React from "react"
import { StyleSheet, Text, View } from "react-native"
import { useTranslation } from "react-i18next"

import { getUserPerceivedOfflineFromState } from "@/redux/network.reducer"
import { useRootSelector } from "@/redux/useRootSelector"
import { COLORS, RADII, SPACING, getFontName } from "@/theme"
import { AppIcons } from "@/theme/icons/AppIcons"
import { SHADOWS } from "@/theme/theme/shadows"

const TEXT_COLOR = COLORS.$textDark.primary

const styles = StyleSheet.create({
  contentContainer: {
    alignItems: "center",
    flexDirection: "row",
    flexWrap: "nowrap",
    justifyContent: "center",
  },
  iconContainer: {
    marginRight: 8,
  },
  root: {
    alignItems: "center",
    backgroundColor: COLORS.$gray[700],
    borderRadius: RADII.$full,
    height: 32,
    justifyContent: "center",
    paddingHorizontal: SPACING.$4,
    position: "absolute",
    right: SPACING.$5,
    top: SPACING.$8,
    ...SHADOWS.$8,
  },
  text: {
    color: TEXT_COLOR,
    fontFamily: getFontName("Poppins_500Medium"),
    fontSize: 14,
    textTransform: "uppercase",
  },
})

/**
 * Loud floating pill that appears in the top-right when a user-initiated
 * request has failed because the device is offline. Reads
 * ``userPerceivedOffline`` from the ``network`` slice — flipped on by
 * ``useErrorHandler`` and cleared when ``isOnline`` flips back to true.
 * The passive cloud icon (``OfflineStatusIcon``) handles the always-on
 * "you have no service" signal; this pill is reserved for the moment the
 * user actually tried to do something.
 *
 * Sits above ``DemoModeIndicator`` (which is at ``top: $28``) so both
 * can coexist without overlap.
 */
export default function OfflineIndicator() {
  const { t } = useTranslation()
  const userPerceivedOffline = useRootSelector(getUserPerceivedOfflineFromState)

  if (!userPerceivedOffline) {
    return null
  }

  return (
    <View style={styles.root}>
      <View style={styles.contentContainer}>
        <View style={styles.iconContainer}>
          <AppIcons.DeviceOffline color={TEXT_COLOR} size={18} />
        </View>
        <Text style={styles.text}>{t("offlineIndicator")}</Text>
      </View>
    </View>
  )
}
